var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useEffect, useRef, useState } from 'react';
import classnames from 'classnames';
import makeIconName from '../../utils/makeIconName';
import makeBodyText from '../../utils/makeText';
import styles from './index.module.scss';
import Tooltip from '../Tooltip';
// eslint-disable-next-line complexity
const Input = (_a) => {
    var { id, onFocus, onBlur, onChange, onClear, onExtraButtonClick, type = 'text', withIcon, value, isControlledMode, label, placeholder, iconName, isFullWidth, isDisabled, isError, description, isDarkBcg, isReadonly, name, showToolTip, autoFocus = false, inputSymbol, autoComplete = 'off' } = _a, others = __rest(_a, ["id", "onFocus", "onBlur", "onChange", "onClear", "onExtraButtonClick", "type", "withIcon", "value", "isControlledMode", "label", "placeholder", "iconName", "isFullWidth", "isDisabled", "isError", "description", "isDarkBcg", "isReadonly", "name", "showToolTip", "autoFocus", "inputSymbol", "autoComplete"]);
    const wrapElem = useRef(null);
    const inputElem = useRef(null);
    const [isEntered, setEnteredState] = useState(autoFocus);
    const [isHovered, setIsHovered] = useState(false);
    const [innerValue, setInnerValue] = useState('');
    const _handleChange = (e) => {
        if (isControlledMode) {
            onChange(e);
        }
        else {
            setInnerValue(e.target.value);
            onChange(e);
        }
    };
    useEffect(() => {
        if (inputElem.current && autoFocus) {
            inputElem.current.focus();
        }
    }, [autoFocus]);
    useEffect(() => {
        if (type !== 'number') {
            return () => { };
        }
        const inputTarget = inputElem.current;
        const handleWheel = (e) => e.preventDefault();
        inputTarget.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            inputTarget.removeEventListener('wheel', handleWheel, { passive: false });
        };
    }, [inputElem]);
    const _handleFocus = (e) => {
        setEnteredState(true);
        onFocus && onFocus(e);
    };
    const _handleBlur = (e) => {
        setEnteredState(false);
        onBlur && onBlur(e);
    };
    const setHover = () => {
        setIsHovered(!isHovered);
    };
    const _handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setEnteredState(false);
        if (!isControlledMode) {
            setInnerValue('');
        }
        onClear ? onClear(e.target.parentNode.firstChild) : onChange({ target: { name, value: '' } });
        setEnteredState(true);
    };
    const _handleExtraButtonClick = (e) => {
        onExtraButtonClick && onExtraButtonClick(e);
        setEnteredState(true);
    };
    const isValue = (isControlledMode && value) || innerValue;
    const inputClassNames = classnames({
        [styles.inputWrap]: true,
        [styles.notOpen]: !isEntered,
        [styles.notOpenWithValue]: isValue && !isEntered,
        [styles.withDarkBcg]: isDarkBcg,
        [styles.filled]: isValue,
        [styles.active]: isEntered,
        [styles.fullWidth]: isFullWidth,
        [styles.disabled]: isDisabled,
        [styles.readOnly]: isReadonly,
        [styles.error]: isError
    });
    const inputFieldClassNames = classnames({
        [styles.inputField]: true,
        [styles.withEditIcon]: withIcon,
        [styles.withClearIcon]: isValue && isEntered,
        [styles.withInputSymbol]: inputSymbol
    });
    const symbolClassNames = classnames({
        [styles.inputSymbol]: true,
        [styles.inputSymbolShow]: isEntered || isValue
    });
    const renderClearButton = () => {
        if (!isValue || !isEntered) {
            return null;
        }
        return (React.createElement("button", { tabIndex: -1, id: `${name}-clear-btn`, type: 'button', "aria-label": 'Clear input field', className: `${makeIconName('cross', 'x24')} ${styles.supportBtn} ${withIcon ? styles.extraIconMargin : ''}`, onMouseDown: (e) => _handleClear(e) }));
    };
    const renderExtraButton = () => {
        if (!withIcon) {
            return null;
        }
        return (React.createElement("button", { tabIndex: -1, type: 'button', "aria-label": iconName, className: `${makeIconName(iconName, 'x24')} ${styles.supportBtn} ${styles.icon}`, onClick: (e) => _handleExtraButtonClick(e) }));
    };
    return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    React.createElement("div", Object.assign({ className: styles.inputSupportWrap }, (isDisabled && showToolTip ?
        {
            onMouseEnter: setHover,
            onMouseLeave: setHover,
            onFocus: setHover,
            onBlur: setHover
        } :
        {})),
        isDisabled && showToolTip && (React.createElement(Tooltip, { useClick: true, withoutChild: true, forceShow: isDisabled && isHovered, label: 'This field isn\'t available' })),
        React.createElement("div", { tabIndex: 1, ref: wrapElem, className: `${inputClassNames} inputWrapperField`, onClick: _handleFocus, onBlur: _handleBlur },
            inputSymbol ? React.createElement("span", { className: symbolClassNames }, inputSymbol) : null,
            React.createElement("input", Object.assign({ tabIndex: 0, autoFocus: !!autoFocus, ref: inputElem, name: name || id, id: id, autoComplete: autoComplete, className: inputFieldClassNames, onChange: _handleChange, type: type, placeholder: isEntered ? placeholder : '', disabled: isDisabled, readOnly: isReadonly, value: isControlledMode ? value : innerValue }, others)),
            React.createElement("label", { htmlFor: id, className: `${makeBodyText('L')} ${styles.label}` }, label),
            renderClearButton(),
            renderExtraButton()),
        description && (React.createElement("p", { id: `${name}-err-text`, className: `${makeBodyText('S')} ${styles.description} ${isError ? styles.withError : ''}` }, description))));
};
export default Input;
